import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  Extension as ExtensionIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { ClassSession, SessionFilters } from '../../../types/tutors.types';
import ClassSessionCard from './ClassSessionCard';
import { apiService } from '../../../services/api';
import useMeetExtensionDetection from '../../../hooks/useExtensionDetector';

const ClassSessions: React.FC = () => {
  const [sessions, setSessions] = useState<ClassSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<SessionFilters>({
    searchTerm: '',
    dateRange: {
      start: null,
      end: null,
    },
    assignmentStatus: 'all',
  });

  const { isDetected: extensionDetected, isChecking: extensionChecking, isValidKey } = useMeetExtensionDetection();

  useEffect(() => {
    // Only fetch sessions if extension is detected and has valid key
    if (extensionDetected && isValidKey) {
      fetchTutorSessions();
    }
  }, [extensionDetected, isValidKey]);

  const fetchTutorSessions = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getTutorSessions();
      setSessions(data);
    } catch (err) {
      console.error('Error fetching tutor sessions:', err);
      setError('Unable to load course assignments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredSessions = useMemo(() => {
    return sessions.filter((session: ClassSession) => {
      // Search filter
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        if (!session.eventTitle.toLowerCase().includes(searchLower)) {
          return false;
        }
      }

      // Assignment status filter
      if (filters.assignmentStatus !== 'all') {
        const hasAssignments = session.assignedDates.length > 0;
        const isFullyAssigned = session.assignedDates.length === session.courseDates.length;
        
        if (filters.assignmentStatus === 'assigned' && !hasAssignments) {
          return false;
        }
        if (filters.assignmentStatus === 'not-assigned' && hasAssignments) {
          return false;
        }
      }

      return true;
    });
  }, [sessions, filters]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilters(prev => ({
      ...prev,
      searchTerm: event.target.value,
    }));
  };

  const handleAssignmentStatusChange = (event: any) => {
    setFilters(prev => ({
      ...prev,
      assignmentStatus: event.target.value,
    }));
  };

  return (
    <Box>
      {/* Search and Filter Bar */}
      <Box sx={{ 
        mb: 3,
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 2,
        alignItems: { xs: 'stretch', md: 'center' }
      }}>
        <Box sx={{ flex: 1, maxWidth: { md: '400px' } }}>
          <TextField
            fullWidth
            placeholder="Search class sessions..."
            value={filters.searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box sx={{ minWidth: { md: '200px' } }}>
          <FormControl fullWidth>
            <InputLabel>Assignment Status</InputLabel>
            <Select
              value={filters.assignmentStatus}
              label="Assignment Status"
              onChange={handleAssignmentStatusChange}
            >
              <MenuItem value="all">All Sessions</MenuItem>
              <MenuItem value="assigned">Assigned</MenuItem>
              <MenuItem value="not-assigned">Not Assigned</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Sessions List */}
      <Box>
        {extensionChecking ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2, alignSelf: 'center' }}>
              Checking for Google Meet Extension...
            </Typography>
          </Box>
        ) : !extensionDetected || !isValidKey ? (
          <Box sx={{ py: 4 }}>
            <Alert 
              severity="error" 
              icon={<ExtensionIcon />}
              sx={{ mb: 2 }}
              action={
                <Button 
                  color="inherit" 
                  size="small" 
                  startIcon={<RefreshIcon />}
                  onClick={() => window.location.reload()}
                >
                  Refresh
                </Button>
              }
            >
              <Typography variant="h6" component="div" sx={{ mb: 1 }}>
                {!extensionDetected ? 'Google Meet Extension not found' : 'Invalid Extension Key'}
              </Typography>
              <Typography variant="body2">
                {!extensionDetected 
                  ? 'The Meet Attendance Tracker extension is required to access class sessions. Please ensure the extension is installed and enabled in your browser.'
                  : 'The Meet Attendance Tracker extension was found but the security key is invalid. Please reinstall the extension.'
                }
              </Typography>
            </Alert>
            <Box sx={{ mt: 3, p: 3, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                Installation Instructions:
              </Typography>
              <Button
                variant="outlined"
                startIcon={<OpenInNewIcon />}
                onClick={() => window.open('https://docs.google.com/document/u/2/d/1HrhmK203xrNfmub7FEknKfVZU2FkJF18kTF4lmmI1aU/edit?usp=sharing', '_blank')}
                sx={{ textTransform: 'none' }}
              >
                Install Guide
              </Button>
            </Box>
          </Box>
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2, alignSelf: 'center' }}>
              Loading class sessions...
            </Typography>
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="error">
              {error}
            </Typography>
          </Box>
        ) : extensionDetected && isValidKey ? (
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="body2">
                ✅ Meet Attendance Tracker extension is active
              </Typography>
            </Alert>
            {filteredSessions.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary">
                  No class sessions found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {sessions.length === 0 
                    ? 'No course assignments available for your account'
                    : 'Try adjusting your search or filter criteria'
                  }
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {filteredSessions.map((session: ClassSession) => (
                  <ClassSessionCard key={session.id} session={session} />
                ))}
              </Box>
            )}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default ClassSessions;
